import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { ReactNode } from "react";

import { authService } from "../services/authService";

// ===============================
// TYPES
// ===============================

export type UserRole =
  | "Admin"
  | "User"
  | "Manager";

export type SessionUser = {
  userID: number;
  name: string;
  email: string;
  role: string;
  userImage: string;
  token: string;
};

type RegisterInput = {
  name: string;
  email: string;
  password: string;
  role: UserRole;
};

type AuthContextValue = {
  user: SessionUser | null;

  login: (
    email: string,
    password: string
  ) => Promise<void>;

  register: (
    input: RegisterInput
  ) => Promise<void>;

  logout: () => void;

  isAuthenticated: boolean;

  loading: boolean;
};

// ===============================
// STORAGE KEYS
// ===============================

const SESSION_KEY =
  "nsj_session";

const TOKEN_KEY =
  "token";

// ===============================
// CONTEXT
// ===============================

const AuthContext =
  createContext<
    AuthContextValue | null
  >(null);

// ===============================
// PROVIDER
// ===============================

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}) {

  const [user, setUser] =
    useState<SessionUser | null>(
      null
    );
  const [loading, setLoading] =
    useState(true);

  // ===============================
  // LOAD SESSION
  // ===============================

  useEffect(() => {

    const session =
      localStorage.getItem(
        SESSION_KEY
      );

    if (session) {

      setUser(
        JSON.parse(session)
      );
    }

    setLoading(false);

  }, []);

  // ===============================
  // LOGIN
  // ===============================

  async function login(
    email: string,
    password: string
  ) {

    try {

      const response =
        await authService.login({
          email,
          password,
        });

      // SAVE SESSION
      localStorage.setItem(
        SESSION_KEY,
        JSON.stringify(response)
      );

      // SAVE TOKEN
      localStorage.setItem(
        TOKEN_KEY,
        response.token
      );

      // UPDATE STATE
      setUser(response);

    } catch (error: any) {

      throw new Error(
        error?.message ||
        "Login failed"
      );
    }
  }

  // ===============================
  // REGISTER
  // ===============================

  async function register(
    input: RegisterInput
  ) {

    try {

      await authService.register(
        input
      );

    } catch (error: any) {

      throw new Error(
        error?.message ||
        "Registration failed"
      );
    }
  }

  // ===============================
  // LOGOUT
  // ===============================

  function logout() {

    localStorage.removeItem(
      SESSION_KEY
    );

    localStorage.removeItem(
      TOKEN_KEY
    );

    setUser(null);
  }

  // ===============================
  // MEMO
  // ===============================

 
  const value = useMemo(
  () => ({
    user,
    login,
    register,
    logout,
    loading,
    isAuthenticated:
  Boolean(user?.token),
  }),
  [user, loading]
);

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ===============================
// HOOK
// ===============================

export function useAuth() {

  const context =
    useContext(AuthContext);

  if (!context) {

    throw new Error(
      "useAuth must be used under AuthProvider"
    );
  }

  return context;
}